// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The four draggable corner handles.
 *
 * Corners are stored normalised to 0–1 rather than in pixels, so the same state
 * survives a window resize, an orientation change and the difference between
 * the on-screen preview size and the full-resolution photo behind it.
 */

import { clampQuad, type Point, type Quad } from './cv/geometry';

const LABELS = ['Top left', 'Top right', 'Bottom right', 'Bottom left'];

export class CornerEditor {
  private layer: HTMLElement;
  private handles: HTMLButtonElement[] = [];
  private quad: Quad;
  private onChange: (quad: Quad) => void;

  constructor(layer: HTMLElement, onChange: (quad: Quad) => void) {
    this.layer = layer;
    this.onChange = onChange;
    this.quad = [
      { x: 0.1, y: 0.1 },
      { x: 0.9, y: 0.1 },
      { x: 0.9, y: 0.9 },
      { x: 0.1, y: 0.9 },
    ];
    this.build();
  }

  private build(): void {
    this.layer.replaceChildren();

    const outline = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    outline.classList.add('corner-outline');
    outline.setAttribute('viewBox', '0 0 100 100');
    outline.setAttribute('preserveAspectRatio', 'none');
    outline.setAttribute('aria-hidden', 'true');

    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    outline.append(polygon);
    this.layer.append(outline);

    for (let i = 0; i < 4; i++) {
      const handle = document.createElement('button');
      handle.type = 'button';
      handle.className = 'corner-handle';
      handle.dataset.index = String(i);
      handle.setAttribute('aria-label', `${LABELS[i]} corner`);
      this.attachDrag(handle, i);
      this.layer.append(handle);
      this.handles.push(handle);
    }

    this.paint();
  }

  private attachDrag(handle: HTMLButtonElement, index: number): void {
    const move = (event: PointerEvent) => {
      const rect = this.layer.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.quad[index] = {
        x: (event.clientX - rect.left) / rect.width,
        y: (event.clientY - rect.top) / rect.height,
      };
      this.quad = clampQuad(this.quad, 1, 1);
      this.paint();
      this.onChange(this.getQuad());
    };

    handle.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      handle.setPointerCapture(event.pointerId);
      handle.classList.add('is-dragging');
    });
    handle.addEventListener('pointermove', (event) => {
      if (!handle.hasPointerCapture(event.pointerId)) return;
      event.preventDefault();
      move(event);
    });

    const end = (event: PointerEvent) => {
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      handle.classList.remove('is-dragging');
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);

    // Arrow keys nudge, so a corner can be placed precisely without a mouse.
    handle.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 0.02 : 0.004;
      const deltas: Record<string, Point> = {
        ArrowLeft: { x: -step, y: 0 },
        ArrowRight: { x: step, y: 0 },
        ArrowUp: { x: 0, y: -step },
        ArrowDown: { x: 0, y: step },
      };
      const delta = deltas[event.key];
      if (!delta) return;
      event.preventDefault();
      this.quad[index] = {
        x: this.quad[index].x + delta.x,
        y: this.quad[index].y + delta.y,
      };
      this.quad = clampQuad(this.quad, 1, 1);
      this.paint();
      this.onChange(this.getQuad());
    });
  }

  private paint(): void {
    for (let i = 0; i < 4; i++) {
      this.handles[i].style.left = `${this.quad[i].x * 100}%`;
      this.handles[i].style.top = `${this.quad[i].y * 100}%`;
    }
    const polygon = this.layer.querySelector('polygon');
    polygon?.setAttribute(
      'points',
      this.quad.map((p) => `${p.x * 100},${p.y * 100}`).join(' '),
    );
  }

  /** Image dimensions the quad is reported in. */
  private imageWidth = 1;
  private imageHeight = 1;

  setImageSize(width: number, height: number): void {
    this.imageWidth = Math.max(1, width);
    this.imageHeight = Math.max(1, height);
  }

  /** Sets the corners from image-space coordinates. */
  setQuad(quad: Quad): void {
    this.quad = clampQuad(
      quad.map((p) => ({ x: p.x / this.imageWidth, y: p.y / this.imageHeight })) as Quad,
      1,
      1,
    );
    this.paint();
  }

  /** Reads the corners back in image-space coordinates. */
  getQuad(): Quad {
    return this.quad.map((p) => ({
      x: p.x * this.imageWidth,
      y: p.y * this.imageHeight,
    })) as Quad;
  }
}
